import { addElementToArray, isDayInsideRange } from '@fold-dev/core'
import { create } from 'zustand'
import {
    addTasktoSection,
    buildTaskTree,
    flattenArray,
    flattenSectionsTasks,
    getSortFunction,
    removeTasks,
    removeTaskSelection,
    removeTasksLabels,
    removeTasksUsers,
    sortSectionTasks,
    updateManyTasks,
    updateMultipleTasks,
    updateTasks,
    updateTasksLabels,
    updateTasksUsers,
} from '../components'
import { emit } from '../contexts/websocket.context'
import { byPosition, hexToOklchHue, setFastCache } from '../helpers'

const snc = (action, payload, roomId) => {
    if (payload.skipHost) delete payload.skipHost
    setTimeout(() => {
        emit(roomId, action, payload)
    })
}

export const useAppStore = create((set: any, get: any) => ({
    unreads: [],
    viewType: '',
    navDisplay: 'projects' as 'projects' | 'messages',
    hideNav: false,
    mainLoading: false,
    filters: {
        sort: null,
        hideComplete: false,
    },
    user: {
        id: '',
        name: '',
        password: '',
        image: '',
        timezone: '',
        location: '',
        language: '',
        description: '',
        title: '',
        username: '',
        email: '',
    },
    workspace: {
        id: '',
        name: '',
        description: '',
        image: '',
        color: '',
        workspaceUserId: '',
        workspaceUserFields: {},
        role: '',
        plan: 0,
        subscriptionId: '',
        subscriptionPlan: '',
        subscriptionQty: 0,
        cancelUrl: '',
        updateUrl: '',
        nextBillDate: '',
        lastPaymentDate: '',
        workHoursStart: '',
        workHoursEnd: '',
        weekLength: 7,
        createdAt: new Date(),
    },
    project: {
        id: '',
        name: '',
        description: '',
        overview: '',
        color: '',
        email: '',
        icon: '',
        position: '',
        completeTaskCount: 0, 
        totalTaskCount: 0,
        archived: false,
        isPrivate: false,
        isClosed: false,
        isChannel: true,
        projectUserId: '',
        joinable: false,
        roadmap: false,
        roadmapDescription: '',
        shortcode: '',
        role: '',
        meta: {},
        teamId: '',
        teamName: '',
        teamColor: '',
        projectUsers: null,
    },
    task: {
        id: '',
        title: '',
        description: '',
        priority: null,
        type: null,
        position: 0,
        complete: false,
        read: true,
        points: 0,
        complexity: null,
        start: null,
        end: null,
        repeat: null,
        wholeDay: false,
        externalEmail: null,
        parentTaskId: null,
        meta: [],
        tasks: [],
        users: [],
        followers: [],
        labels: [],
        attachments: [],
        likes: 0,
        projectId: '',
        projectSectionId: '',
        projectSectionTitle: '',
        projectName: '',
        teamId: '',
    },
    thread: {
        id: null,
        threadId: null,
        messages: [],
    },
    threads: [],
    labels: [],
    projects: [],
    teams: [],
    subtasks: [],
    tasks: [],
    sections: [],
    comments: [],
    messages: [],
    sidebar: <null | 'inbox' | 'messages' | 'overview' | 'thread' | 'tasks' | 'calendar'>null,
    notifications: [],
    starred: [],
    actions: {
        // -----------------------
        // loading
        // -----------------------
        setMainLoading(mainLoading) {
            set({ mainLoading })
        },
        // -----------------------
        // unreads
        // -----------------------
        setUnreads(unreads) {
            set({ unreads })
        },
        unreadsAdd(unread) {
            set({
                unreads: [...get().unreads, unread],
            })
        },
        unreadsDelete(unread) {
            set({
                unreads: [...get().unreads.filter((u) => u.projectId != unread)],
            })
        },
        // -----------------------
        // nav
        // -----------------------
        setNavDisplay(navDisplay) {
            set({ navDisplay })
        },
        setHideNav(hideNav) {
            set({ hideNav })
        },
        // -----------------------
        // labels
        // -----------------------
        setLabels(labels) {
            set({ labels })
        },
        labelsAdd(newLabel, sync = true) {
            set({
                labels: [...get().labels, newLabel],
            })

            if (sync) snc('labelsAdd', newLabel, get().workspace.id)
        },
        labelsUpdate(updatedLabel, sync = true) {
            set({
                labels: get().labels.map((label) =>
                    label.id === updatedLabel.id ? { ...label, ...updatedLabel } : label
                ),
                starred: get().starred.map((label) =>
                    label.id === updatedLabel.id ? { ...label, ...updatedLabel } : label
                ),
            })

            if (sync) snc('labelsUpdate', updatedLabel, get().workspace.id)
        },
        labelsDelete(labelId, sync = true) {
            set({
                labels: get().labels.filter((label) => label.id !== labelId),
            })

            if (sync) snc('labelsDelete', labelId, get().workspace.id)
        },
        // -----------------------
        // filters
        // -----------------------
        setFilters(filters) {
            // temp until we sort out sorting
            set({ filters: { ...filters, sort: null } })
        },
        setViewType(viewType: 'project' | 'upcoming' | 'today' | 'unassigned' | 'calendar') {
            set({ viewType })
        },
        // -----------------------
        // user
        // -----------------------
        setUser(user, sync = true) {
            set({ user })
            if (sync) snc('setUser', user, get().user.id)
        },
        userUpdate(user, sync = true) {
            set({ user: { ...get().user, ...user } })

            if (sync) snc('userUpdate', user, get().user.id)
        },
        // -----------------------
        // workspace
        // -----------------------
        setWorkspace(workspace) {
            if (!!workspace.color) {
                document.documentElement.style.setProperty('--hue', hexToOklchHue(workspace.color))
            } else {
                document.documentElement.style.removeProperty('--hue')
            }

            set({ workspace })
        },
        workspaceUpdate(workspace, sync = true) {
            set({ workspace: { ...get().workspace, ...workspace } })

            if (!!workspace.color) {
                document.documentElement.style.setProperty('--hue', hexToOklchHue(workspace.color))
            } else {
                document.documentElement.style.removeProperty('--hue')
            }

            if (sync) snc('workspaceUpdate', workspace, get().workspace.id)
        },
        workspaceUserUpdate(workspace, sync = true) {
            set({ workspace: { ...get().workspace, ...workspace } })
            if (sync) snc('workspaceUpdate', workspace, get().user.id)
        },
        // -----------------------
        // notifications
        // -----------------------
        setNotifications(notifications) {
            set({ notifications })
        },
        notificationAdd(notification) {
            const { notifications } = get()
            const projectNotifications = notifications[notification.projectId] || []

            set({
                notifications: {
                    ...notifications,
                    [notification.projectId]: [notification, ...projectNotifications],
                },
            })
        },
        // -----------------------
        // starred
        // -----------------------
        setStarred(starred) {
            set({ starred })
        },
        starredAdd(starred, sync = true) {
            set({ starred: [starred, ...get().starred] })

            if (sync) snc('starredAdd', starred, get().user.id)
        },
        starredDelete(projectId, sync = true) {
            set({ starred: get().starred.filter((pid) => pid !== projectId) })

            if (sync) snc('starredDelete', projectId, get().user.id)
        },
        // -----------------------
        // projects
        // -----------------------
        setProject(project) {
            set({ project })
        },
        setProjects(projects) {
            set({ projects })
        },
        projectAdd(newProject, sync = true) {
            set({
                projects: [...get().projects, newProject],
            })

            if (sync) snc('projectAdd', newProject, get().workspace.id)
        },
        projectUpdate(updatedProject, sync = true) {
            const state = get()

            set({
                project:
                    updatedProject.id == state.project.id ? { ...state.project, ...updatedProject } : state.project,
                projects: state.projects.map((project) =>
                    project.id === updatedProject.id ? { ...project, ...updatedProject } : project
                ),
                starred: state.starred.map((project) =>
                    project.id === updatedProject.id ? { ...project, ...updatedProject } : project
                ),
            })

            if (sync) snc('projectUpdate', updatedProject, get().workspace.id)
        },
        projectDelete(projectId, sync = true) {
            set({
                projects: get().projects.filter((project) => project.id !== projectId),
            })

            if (sync) snc('projectDelete', projectId, get().workspace.id)
        },
        projectUsersAdd(payload, sync = true) {
            const { projectUser, projectId } = payload
            const state = get()

            set({
                project:
                    state.project.id == projectId
                        ? {
                              ...state.project,
                              projectUsers: [...(state.project.projectUsers || []), projectUser],
                          }
                        : state.project,
                projects: state.projects.map((project) => {
                    if (project.id == projectId) {
                        return {
                            ...project,
                            projectUsers: [...(project.projectUsers || []), projectUser],
                        }
                    } else {
                        return project
                    }
                }),
            })

            if (sync) snc('projectUsersAdd', payload, get().project.id)
        },
        projectUsersDelete(payload, sync = true) {
            const { projectUser, projectId } = payload
            const state = get()

            set({
                project:
                    state.project.id == projectId
                        ? {
                              ...state.project,
                              projectUsers: (state.project.projectUsers || []).filter((pu) => pu.id != projectUser.id),
                          }
                        : state.project,
                projects: state.projects.map((project) => {
                    if (project.id == projectId) {
                        return {
                            ...project,
                            projectUsers: (project.projectUsers || []).filter((pu) => pu.id != projectUser.id),
                        }
                    } else {
                        return project
                    }
                }),
            })

            if (sync) snc('projectUsersDelete', payload, get().project.id)
        },
        // -----------------------
        // comments
        // -----------------------
        setComments(comments) {
            set({ comments })
        },
        commentAdd(newComment, sync = true) {
            set({
                comments: [...get().comments, newComment],
            })

            if (sync) snc('commentAdd', newComment, get().task.id)
        },
        commentUpdate(updatedComment, sync = true) {
            set({
                comments: get().comments.map((comment) =>
                    comment.id === updatedComment.id ? { ...comment, ...updatedComment } : comment
                ),
            })

            if (sync) snc('commentUpdate', updatedComment, get().task.id)
        },
        commentDelete(commentId, sync = true) {
            set({
                comments: get().comments.filter((comment) => comment.id !== commentId),
            })

            if (sync) snc('commentDelete', commentId, get().task.id)
        },
        commentAttachmentRemove(payload, sync = true) {
            const { id } = payload

            set({
                comments: get().comments.map((comment) => {
                    return {
                        ...comment,
                        attachments: (comment.attachments || []).filter((attachment) => attachment.id != id),
                    }
                }),
            })

            if (sync) snc('commentAttachmentRemove', payload, get().task.id)
        },
        // -----------------------
        // sidebar
        // -----------------------
        setSidebar(sidebar) {
            if (sidebar != 'messages') {
                set({ sidebar })
            } else {
                set({ sidebar })
            }
        },
        // -----------------------
        // thread
        // -----------------------
        setThread(thread) {
            set({ thread })
        },
        setThreadId(threadId) {
            set({ thread: { ...get().thread, threadId } })
        },
        // -----------------------
        // threads
        // -----------------------
        setThreads(threads) {
            set({ threads })
        },
        threadsAdd(newThread, sync = true) {
            set({
                threads: [...get().threads, newThread],
            })

            if (sync) snc('threadsAdd', newThread, get().user.id)
        },
        threadsUpdate(updatedThread, sync = true) {
            set({
                threads: get().threads.map((thread) =>
                    thread.id === updatedThread.id ? { ...thread, ...updatedThread } : thread
                ),
            })

            if (sync) snc('threadsUpdate', updatedThread, get().user.id)
        },
        threadsDelete(threadId, sync = true) {
            set({
                threads: get().threads.filter((thread) => thread.id !== threadId),
            })

            if (sync) snc('threadsDelete', threadId, get().user.id)
        },
        // -----------------------------
        // messages / thread messages
        // -----------------------------
        setMessages(messages) {
            set({ messages })
        },
        messageAdd(newMessage, sync = true) {
            const state = get()
            const { isThreadMessage } = newMessage

            // which means we are in thread
            if (!!state.thread.id && !!newMessage.parentId && newMessage.parentId == state.thread.id) {
                set({
                    thread: {
                        ...state.thread,
                        messages: [...state.thread.messages, newMessage],
                    },
                })
            } else {
                if (!isThreadMessage) {
                    set({
                        messages: [...state.messages, newMessage],
                    })
                }
            }

            if (sync) snc('messageAdd', newMessage, state.project.id)
        },
        messageUpdate(updatedMessage, sync = true) {
            const state = get()

            set({
                messages: state.messages.map((message) =>
                    message.id === updatedMessage.id ? { ...message, ...updatedMessage } : message
                ),
                thread: {
                    ...state.thread,
                    messages: state.thread.messages.map((message) =>
                        message.id === updatedMessage.id ? { ...message, ...updatedMessage } : message
                    ),
                },
            })

            if (sync) snc('messageUpdate', updatedMessage, state.project.id)
        },
        messageDelete(messageId, sync = true) {
            const state = get()

            set({
                messages: state.messages.filter((message) => message.id !== messageId),
                thread: {
                    ...state.thread,
                    messages: state.thread.messages.filter((message) => message.id !== messageId),
                },
            })

            if (sync) snc('messageDelete', messageId, state.project.id)
        },
        messageReplyCountIncrease(messageId, sync = true) {
            const state = get()

            set({
                messages: state.messages.map((message) => {
                    if (messageId == message.id) {
                        return {
                            ...message,
                            totalReplies: message.totalReplies == null ? 1 : +message.totalReplies + 1,
                        }
                    } else {
                        return message
                    }
                }),
            })

            if (sync) snc('messageReplyCountIncrease', messageId, state.project.id)
        },
        messageReplyCountDecrease(messageId, sync = true) {
            const state = get()

            set({
                messages: state.messages.map((message) => {
                    if (messageId == message.id) {
                        return {
                            ...message,
                            totalReplies: message.totalReplies == null ? 0 : +message.totalReplies - 1,
                        }
                    } else {
                        return message
                    }
                }),
            })

            if (sync) snc('messageReplyCountDecrease', messageId, state.project.id)
        },
        messageAttachmentRemove(payload, sync = true) {
            const { id } = payload
            const state = get()

            set({
                thread: {
                    ...state.thread,
                    messages: state.thread.messages.map((message) => ({
                        ...message,
                        attachments: (message.attachments || []).filter((attachment) => attachment.id != id),
                    })),
                },
                messages: state.messages.map((message) => ({
                    ...message,
                    attachments: (message.attachments || []).filter((attachment) => attachment.id != id),
                })),
            })

            if (sync) snc('messageAttachmentRemove', payload, state.project.id)
        },
        // -----------------------
        // teams
        // -----------------------
        setTeams(teams) {
            set({ teams: teams.sort(byPosition) })
        },
        teamAdd(newTeam, sync = true) {
            set({
                teams: [...get().teams, newTeam].sort(byPosition),
            })

            if (sync) snc('teamAdd', newTeam, get().workspace.id)
        },
        teamUpdate(updatedTeam, sync = true) {
            set({
                teams: get()
                    .teams.map((team) => (team.id === updatedTeam.id ? { ...team, ...updatedTeam } : team))
                    .sort(byPosition),
            })

            if (sync) snc('teamUpdate', updatedTeam, get().workspace.id)
        },
        teamDelete(teamId, sync = true) {
            set({
                teams: get().teams.filter((team) => team.id !== teamId),
            })

            if (sync) snc('teamDelete', teamId, get().workspace.id)
        },
        // -----------------------
        // sections
        // -----------------------
        setSections(sections, cache = true) {
            const state = get()
            const sortFunction = getSortFunction(state.filters.sort)
            const sortedSections = sortFunction ? sortSectionTasks(sections, sortFunction) : sections

            if (cache) setFastCache(state.project.id, sortedSections)

            set({ sections: sortedSections })
        },
        sectionDelete(sectionId, sync = true) {
            set({ sections: get().sections.filter((section) => sectionId != section.id) })

            if (sync) snc('sectionDelete', sectionId, get().project.id || get().workspace.id)
        },
        sectionUpdate(section, sync = true) {
            set({ sections: get().sections.map((s) => (section.id == s.id ? { ...s, ...section } : s)) })

            if (sync) snc('sectionUpdate', section, get().project.id || get().workspace.id)
        },
        sectionAdd(payload, sync = true) {
            const { section, targetIndex } = payload
            const { id, title, position } = section

            set({
                sections: addElementToArray(get().sections, targetIndex, {
                    id,
                    title,
                    position,
                    tasks: [],
                }),
            })

            if (sync) snc('sectionAdd', payload, get().project.id || get().workspace.id)
        },
        sectionMove(payload, sync = true) {
            const { projectSectionId, position } = payload
            const sections = get().sections.map((section) =>
                section.id == projectSectionId ? { ...section, position: +position } : section
            )

            set({ sections: [...sections.sort(byPosition)] })

            if (sync) snc('sectionMove', payload, get().project.id || get().workspace.id)
        },
        // -----------------------
        // task modal
        // -----------------------
        setTask(task) {
            set({ task: { ...get().task, ...task } })
        },
        // -----------------------
        // tasks
        // -----------------------
        buildSectionHierarchy(sections, tasks) {
            const state = get()
            const type = state.viewType
            const actions = state.actions

            if (type == 'unassigned') {
                actions.setSections(
                    sections.map((section, index) => ({
                        ...section,
                        isNoSection: index == 0,
                        locked: true,
                        tasks: buildTaskTree(tasks.filter(({ projectId }) => !projectId).sort(byPosition)),
                    }))
                )
            }

            if (type == 'calendar') {
                actions.setSections(
                    sections.map((section, index) => ({
                        ...section,
                        isNoSection: index == 0,
                        locked: true,
                        tasks: buildTaskTree(tasks),
                    }))
                )
            }

            if (type == 'today') {
                actions.setSections(
                    sections.map((section, index) => ({
                        ...section,
                        isNoSection: index == 0,
                        locked: true,
                        tasks: buildTaskTree(
                            tasks.filter(({ start, end }) =>
                                isDayInsideRange(new Date(start), [new Date(section.day), new Date(section.day)])
                            )
                        ),
                    }))
                )
            }

            if (type == 'upcoming') {
                actions.setSections(
                    sections.map((section, index) => ({
                        ...section,
                        isNoSection: index == 0,
                        locked: true,
                        tasks: buildTaskTree(
                            tasks.filter(({ start, end }) =>
                                isDayInsideRange(new Date(start), [new Date(section.day), new Date(section.day)])
                            )
                        ),
                    }))
                )
            }

            if (type == 'project') {
                actions.setSections(
                    sections.map((section, index) => ({
                        ...section,
                        isNoSection: index == 0,
                        locked: index == 0,
                        position: +section.position,
                        tasks: buildTaskTree(
                            tasks
                                .filter((task) => task.projectSectionId == section.id)
                                .sort(getSortFunction(state.filters.sort))
                        ),
                    }))
                )
            }
        },
        taskUpdate(payload, sync = true) {
            const state = get()
            const isToday = state.viewType == 'today'
            const isUpcoming = state.viewType == 'upcoming'
            const { task } = payload
            const { start } = task
            const shouldRebuildHierarchy = !!start && (isToday || isUpcoming)
            const sortFunction = getSortFunction(state.filters.sort)
            const isUpdatingComplete = payload.complete != null && payload.complete != undefined

            if (state.task.id == task.id) set({ task: { ...state.task, ...task } })

            const subtasks = updateTasks(task, state.subtasks)
            const sections = sortFunction
                ? sortSectionTasks(updateTasks(task, state.sections), sortFunction)
                : updateTasks(task, state.sections)

            if (shouldRebuildHierarchy) {
                state.actions.buildSectionHierarchy(state.sections, flattenSectionsTasks(sections))
            } else {
                set({ subtasks, sections })
            }

            if (sync) snc('taskUpdate', payload, get().project.id || get().workspace.id)
        },
        taskUpdateMany(payload, sync = true) {
            const { task, taskIds } = payload
            const state = get()

            const subtasks = updateMultipleTasks(task, taskIds, state.subtasks)
            const sections = updateMultipleTasks(task, taskIds, state.sections)
            const tasks = sections.reduce((acc, { tasks }) => [...acc, ...flattenArray(tasks)], [])

            set({
                subtasks,
                task: taskIds.includes(state.task.id) ? { ...state.task, ...task } : state.task,
            })

            state.actions.buildSectionHierarchy(sections, tasks)

            if (sync) snc('taskUpdateMany', payload, state.project.id || state.workspace.id)
        },
        taskUpdateProject(payload, sync = true) {
            const { taskIds, task } = payload
            const state = get()
            const sortFunction = getSortFunction(state.filters.sort)

            set({
                subtasks: updateMultipleTasks(task, taskIds, state.subtasks),
                sections: sortFunction
                    ? sortSectionTasks(updateMultipleTasks(task, taskIds, state.sections), sortFunction)
                    : updateMultipleTasks(task, taskIds, state.sections),
            })

            // if the current open task is part of the update set
            // then update it as well
            if (taskIds.includes(state.task.id)) {
                set({
                    task: {
                        ...state.task,
                        ...task,
                    },
                })
            }

            if (sync) snc('taskUpdateProject', payload, get().project.id || get().workspace.id)
        },
        taskUpdateRemoveUser(payload, sync = true) {
            const { userId } = payload

            set({
                sections: get().sections.map((section) => ({
                    ...section,
                    tasks: removeTasksUsers(section.tasks, userId),
                })),
                subtasks: removeTasksUsers(get().subtasks, userId),
            })

            if (sync) snc('taskUpdateRemoveUser', payload, get().project.id || get().workspace.id)
        },
        taskUpdateUpdateUser(payload, sync = true) {
            const { user } = payload

            set({
                sections: get().sections.map((section) => ({
                    ...section,
                    tasks: updateTasksUsers(section.tasks, user),
                })),
                subtasks: updateTasksUsers(get().subtasks, user),
            })

            if (sync) snc('taskUpdateUpdateUser', payload, get().project.id || get().workspace.id)
        },
        taskUpdateRemoveLabel(payload, sync = true) {
            const { labelId } = payload

            set({
                sections: get().sections.map((section) => ({
                    ...section,
                    tasks: removeTasksLabels(section.tasks, labelId),
                })),
                subtasks: removeTasksLabels(get().subtasks, labelId),
            })

            if (sync) snc('taskUpdateRemoveLabel', payload, get().project.id || get().workspace.id)
        },
        taskUpdateUpdateLabel(payload, sync = true) {
            const { label } = payload

            set({
                sections: get().sections.map((section) => ({
                    ...section,
                    tasks: updateTasksLabels(section.tasks, label),
                })),
                subtasks: updateTasksLabels(get().subtasks, label),
            })

            if (sync) snc('taskUpdateUpdateLabel', payload, get().project.id || get().workspace.id)
        },
        taskDelete(payload, sync = true) {
            const { task } = payload

            set({
                sections: get().sections.map((section) => ({ ...section, tasks: removeTasks(task, section.tasks) })),
                subtasks: removeTasks(task, get().subtasks),
            })

            if (sync) snc('taskDelete', payload, get().project.id || get().workspace.id)
        },
        taskDeleteMany(payload, sync = true) {
            const { taskIds } = payload
            const state = get()

            set({
                sections: state.sections.map((section) => ({
                    ...section,
                    tasks: removeTaskSelection(taskIds, section.tasks),
                })),
                subtasks: removeTaskSelection(taskIds, state.subtasks),
            })

            if (sync) snc('taskDeleteMany', payload, state.project.id || state.workspace.id)
        },
        taskAddAndRebuild(payload, sync = true) {
            const { tasks, syncWithProjectId, skipHost } = payload
            const state = get()

            if (!skipHost) {
                const newTasks = [...flattenSectionsTasks(state.sections), ...tasks]
                state.actions.buildSectionHierarchy(state.sections, newTasks)
            }

            if (sync) snc('taskAddAndRebuild', payload, syncWithProjectId || state.workspace.id)
        },
        taskAdd(payload, sync = true) {
            const { sectionId, task, forceSort } = payload
            const state = get()
            const sortFunction = forceSort || getSortFunction(state.filters.sort)
            const sectionIndex = state.sections.findIndex((section) => section.id == sectionId)

            if (sectionIndex == -1) return

            set({
                sections: sortFunction
                    ? sortSectionTasks(addTasktoSection(state.sections, sectionIndex, task), sortFunction)
                    : addTasktoSection(state.sections, sectionIndex, task),
            })

            if (sync) snc('taskAdd', payload, get().project.id || get().workspace.id)
        },
        taskMove(payload, sync = true) {
            const { tasks } = payload
            const state = get()
            const updatedTasks = updateManyTasks(flattenSectionsTasks(state.sections), tasks)

            state.actions.buildSectionHierarchy(state.sections, updatedTasks)
            //set({ subtasks: buildTaskTree(updateManyTasks(flattenArray(state.subtasks), tasks).sort(byPosition)) })

            if (sync) snc('taskMove', payload, state.workspace.id)
        },
        // -----------------------
        // subtasks
        // -----------------------
        setSubtasks(subtasks) {
            set({ subtasks })
        },
        subtaskAdd(payload, sync = true) {
            const state = get()
            const tasks = [...state.subtasks, payload]

            set({ subtasks: tasks })

            if (sync) snc('subtaskAdd', payload, get().project.id || get().workspace.id)
        },
        subtaskAddMultiple(multipleTasks, sync = true) {
            const state = get()
            const tasks = [...state.subtasks, ...multipleTasks]

            set({ subtasks: tasks })

            if (sync) snc('subtaskAddMultiple', multipleTasks, get().project.id || get().workspace.id)
        },
        subtaskMove(payload, sync = true) {
            const { tasks, syncTasks } = payload
            const state = get()
            const updatedTasks = updateManyTasks(flattenArray(state.subtasks), tasks).sort(byPosition)

            set({ subtasks: buildTaskTree(updatedTasks) })

            state.actions.buildSectionHierarchy(
                state.sections,
                updateManyTasks(flattenSectionsTasks(state.sections), tasks)
            )

            if (sync) snc('subtaskMove', payload, state.project.id || state.workspace.id)
        },
    },
}))
